// src/pages/Admin/Finance/PaymentSubmissions.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "@/utils/axios";
import { timeAgo } from "@/utils/timeAgo";
import cls from "./PaymentSubmissions.module.scss";

const DEFAULT_LIMIT = 20;

const statusLabel = (s) => {
  if (s === "pending") return "Pending";
  if (s === "approved") return "Approved";
  if (s === "decline") return "Declined";
  return String(s || "Unknown");
};

const PaymentSubmissions = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [submissions, setSubmissions] = useState([]);
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({
    total: 0,
    totalPages: 1,
    hasNextPage: false,
    hasPrevPage: false,
  });

  useEffect(() => {
    let isMounted = true;

    const fetchSubmissions = async () => {
      try {
        setLoading(true);
        setError("");

        const res = await api.get("/v1/admin/finance/payment-submissions", {
          params: { page, limit: DEFAULT_LIMIT },
        });

        if (!isMounted) return;

        setSubmissions(res.data?.data || []);
        setMeta({
          total: res.data?.total || 0,
          totalPages: res.data?.totalPages || 1,
          hasNextPage: res.data?.hasNextPage || false,
          hasPrevPage: res.data?.hasPrevPage || false,
        });
      } catch (err) {
        if (!isMounted) return;
        const msg =
          err?.response?.data?.message ||
          err?.message ||
          "Failed to load payment submissions";
        setError(msg);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchSubmissions();
    return () => {
      isMounted = false;
    };
  }, [page]);

  const summary = useMemo(() => {
    const pending = submissions.filter((s) => s.status === "pending").length;
    const approved = submissions.filter((s) => s.status === "approved").length;
    const declined = submissions.filter((s) => s.status === "decline").length;
    return { pending, approved, declined };
  }, [submissions]);

  const handleView = (submission) => {
    if (!submission?.userId || !submission?.id) return;
    navigate(
      `/admin/members/single/payment-info/single/${submission.userId}/${submission.id}`,
    );
  };

  return (
    <div className={cls.page}>
      <header className={cls.header}>
        <div className={cls.headerLeft}>
          <p className={cls.eyebrow}>Finance • Payment submissions</p>
          <h1 className={cls.title}>Member payment submissions</h1>
          <p className={cls.subtitle}>
            Review proofs, confirm payments, and jump into the approval flow.
          </p>
        </div>

        <div className={cls.summaryCards}>
          <div className={cls.summaryCard}>
            <span className={cls.summaryLabel}>Total</span>
            <span className={cls.summaryValue}>{meta.total}</span>
          </div>
          <div className={cls.summaryCard}>
            <span className={cls.summaryLabel}>Pending</span>
            <span className={`${cls.summaryValue} ${cls.pending}`}>
              {summary.pending}
            </span>
          </div>
          <div className={cls.summaryCard}>
            <span className={cls.summaryLabel}>Approved</span>
            <span className={`${cls.summaryValue} ${cls.approved}`}>
              {summary.approved}
            </span>
          </div>
          <div className={cls.summaryCard}>
            <span className={cls.summaryLabel}>Declined</span>
            <span className={`${cls.summaryValue} ${cls.declined}`}>
              {summary.declined}
            </span>
          </div>
        </div>
      </header>

      {loading && (
        <div className={cls.stateBox}>
          <div className={cls.loader} />
          <span>Loading payment submissions…</span>
        </div>
      )}

      {!loading && error && (
        <div className={cls.stateBoxError}>
          <span className={cls.stateLabel}>Error</span>
          <p className={cls.stateMessage}>{error}</p>
        </div>
      )}

      {!loading && !error && submissions.length === 0 && (
        <div className={cls.stateBoxEmpty}>
          <h2 className={cls.emptyTitle}>No submissions yet</h2>
          <p className={cls.emptyText}>
            Payment submissions from members will appear here as they come in.
          </p>
        </div>
      )}

      {!loading && !error && submissions.length > 0 && (
        <section className={cls.tableShell}>
          <div className={cls.tableGlow} aria-hidden="true" />
          <div className={cls.tableWrap}>
            <table className={cls.table}>
              <thead>
                <tr>
                  <th className={cls.colMember}>Member</th>
                  <th className={cls.colTitle}>Submission</th>
                  <th className={cls.colAction}>Action</th>
                </tr>
              </thead>

              <tbody>
                {submissions.map((s) => {
                  const username = s.username || "Unknown member";
                  const avatarUrl = s.avatar?.url || "";
                  const optionTitle =
                    s.paymentOption?.title || "Payment option";
                  const initials = username
                    .split(" ")
                    .filter(Boolean)
                    .map((w) => w[0])
                    .join("")
                    .slice(0, 2)
                    .toUpperCase();

                  const st = String(s.status || "").toLowerCase();
                  const statusCls = cls[`status_${st}`] || cls.status_default;

                  return (
                    <tr key={s.id} className={cls.row}>
                      {/* Member */}
                      <td className={cls.cellMember} data-label="Member">
                        <Link
                          to={`/admin/members/single/${s.userId}`}
                          className={cls.memberLink}
                          aria-label={`View ${username} profile`}
                        >
                          <div className={cls.member}>
                            <div className={cls.avatarWrap}>
                              {avatarUrl ? (
                                <img
                                  src={avatarUrl}
                                  alt={username}
                                  className={cls.avatar}
                                  loading="lazy"
                                />
                              ) : (
                                <span className={cls.avatarFallback}>
                                  {initials || "U"}
                                </span>
                              )}
                            </div>

                            <div className={cls.memberText}>
                              <div className={cls.username}>{username}</div>
                              <div className={cls.miniHint}>View profile</div>
                            </div>
                          </div>
                        </Link>
                      </td>

                      {/* Title + Status + time */}
                      <td className={cls.cellTitle} data-label="Submission">
                        <div className={cls.submission}>
                          <div className={cls.submissionTop}>
                            <div className={cls.submissionTitle}>
                              {optionTitle}
                            </div>
                            <span className={`${cls.status} ${statusCls}`}>
                              {statusLabel(st)}
                            </span>
                          </div>
                          <div className={cls.submitted}>
                            Submitted {timeAgo(s.createdAt)}
                          </div>
                        </div>
                      </td>

                      {/* Action */}
                      <td className={cls.cellAction} data-label="Action">
                        <button
                          type="button"
                          className={cls.viewBtn}
                          onClick={() => handleView(s)}
                          aria-label={`View ${username} submission`}
                        >
                          View
                          <span className={cls.viewIcon} aria-hidden="true">
                            ↗
                          </span>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {!loading && !error && submissions.length > 0 && (
        <div className={cls.pagination}>
          <button
            type="button"
            className={cls.pageButton}
            onClick={() => setPage((p) => Math.max(p - 1, 1))}
            disabled={!meta.hasPrevPage}
          >
            ← Previous
          </button>

          <div className={cls.pageInfo}>
            Page <strong>{page}</strong> of <strong>{meta.totalPages}</strong>
          </div>

          <button
            type="button"
            className={cls.pageButton}
            onClick={() => setPage((p) => Math.min(p + 1, meta.totalPages))}
            disabled={!meta.hasNextPage}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
};

export default PaymentSubmissions;
